import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * Stock sort (Atlanta Hobby): when the grid carries data-ah-stock-groups, products load in stock
   * first, then ships from warehouse, then out of stock, one Search & Discovery filter value at a time.
   * @type {{ param: string, groups: { value: string, lastPage: number }[], base: URL, key: string } | null}
   */
  #stockSort = null;

  /** Bumped on every (re)start so fetches from an older filter state are dropped. */
  #stockSortGeneration = 0;

  #stockSortStarting = false;

  /** @type {MutationObserver | undefined} */
  #stockSortObserver;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#checkStockSort();
    this.#stockSortObserver = new MutationObserver(() => this.#checkStockSort());
    this.#stockSortObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-ah-stock-groups'],
    });

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#stockSortObserver?.disconnect();
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL, group?: number } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    if (pageInfo.group !== undefined) {
      const group = this.#stockSort?.groups[pageInfo.group];
      return !!group && pageInfo.page >= 1 && pageInfo.page <= group.lastPage;
    }

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url, page.group);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @param {number} [group] - Stock sort group index, when stock sort is on
   */
  async #fetchSpecificPage(pageNumber, url = undefined, group = undefined) {
    const pageInfo = { page: pageNumber, url, group };
    const generation = this.#stockSortGeneration;

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    if (group !== undefined && generation !== this.#stockSortGeneration) return;
    this.pages.set(this.#pageKey(pageNumber, group), pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page, nextPage.group);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page, nextPage.group);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    await yieldToMainThread();

    // Stock sort pages carry a filter in their URL; keep the address bar on the unfiltered collection.
    if (nextPage.group === undefined) history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL, group?: number } | undefined}
   */
  #getPage(type) {
    if (this.#stockSort) return this.#getStockSortPage(type);

    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @param {number} [group] - Stock sort group index, when stock sort is on
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page, group = undefined) {
    const pageHTML = this.pages.get(this.#pageKey(page, group));

    if (!pageHTML) return;

    return this.#parseCards(pageHTML, group);
  }

  /**
   * @param {string} pageHTML - Section HTML for one page
   * @param {number} [group] - Stock sort group index to tag the cards with
   * @returns {NodeListOf<Element> | undefined}
   */
  #parseCards(pageHTML, group = undefined) {
    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    const cards = gridElement.querySelectorAll(':scope > [ref="cards[]"]');
    if (group !== undefined) {
      for (const card of cards) card.setAttribute('data-ah-group', String(group));
    }
    return cards;
  }

  /**
   * @param {number} page
   * @param {number} [group]
   * @returns {number | string}
   */
  #pageKey(page, group = undefined) {
    return group === undefined ? page : `${group}:${page}`;
  }

  /**
   * Starts stock sort when the server marked the grid for it and its cards are still the unsorted
   * first page (initial load, or after the filters re-rendered the section).
   */
  #checkStockSort() {
    const grid = this.querySelector('[ref="grid"]');
    const spec = grid instanceof HTMLElement ? grid.dataset.ahStockGroups : undefined;

    if (!grid || !spec) {
      if (this.#stockSort) {
        this.#stockSort = null;
        this.#stockSortGeneration++;
        this.#stockSortStarting = false;
      }
      return;
    }
    if (grid.hasAttribute('data-ah-stock-off')) return;

    // Already sorted (or sorting) for the filters in the address bar
    const firstCard = grid.querySelector(':scope > [ref="cards[]"]');
    const current = this.#stockSort;
    if (
      current &&
      current.key === this.#stockSortKey() &&
      (this.#stockSortStarting || firstCard?.hasAttribute('data-ah-group'))
    ) {
      return;
    }

    this.#startStockSort(/** @type {HTMLElement} */ (grid), spec);
  }

  /**
   * Replaces the unsorted first page with the first page of the first non-empty group.
   * On any failure the grid is left as the server rendered it and paging works as normal.
   * @param {HTMLElement} grid
   * @param {string} spec - "in_stock:3 in_warehouse:1 out_of_stock:2" (value:pages)
   */
  async #startStockSort(grid, spec) {
    const generation = ++this.#stockSortGeneration;
    const groups = spec
      .split(' ')
      .map((part) => {
        const [value = '', pages = '0'] = part.split(':');
        return { value, lastPage: Number(pages) || 0 };
      })
      .filter((group) => group.value && group.lastPage > 0);
    const param = grid.dataset.ahStockParam;

    const stop = () => {
      this.#stockSort = null;
      grid.setAttribute('data-ah-stock-off', '');
      grid.setAttribute('data-ah-stock-ready', '');
    };

    if (!param || !groups.length) {
      stop();
      return;
    }

    this.#stockSortStarting = true;
    this.#stockSort = { param, groups, base: new URL(window.location.href), key: this.#stockSortKey() };
    this.pages.clear();

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, this.#stockSortUrl(0, 1));
      if (generation !== this.#stockSortGeneration) return;

      const cards = this.#parseCards(html, 0);
      if (!cards || !cards.length) throw new Error('Stock sort: no products in the first group');

      for (const card of grid.querySelectorAll(':scope > [ref="cards[]"]')) card.remove();
      grid.append(...cards);
      this.#aspectRatioHelper?.processNewElements();
      grid.setAttribute('data-ah-stock-ready', '');
    } catch (error) {
      if (generation !== this.#stockSortGeneration) return;
      console.warn(error);
      stop();
    } finally {
      if (generation === this.#stockSortGeneration) this.#stockSortStarting = false;
    }

    if (generation === this.#stockSortGeneration && this.#stockSort) {
      this.#observeViewMore();
      this.#fetchPage('next');
    }
  }

  /**
   * The address bar without page or hash, to tell whether the filters changed.
   * @returns {string}
   */
  #stockSortKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.hash = '';
    return url.toString();
  }

  /**
   * @param {number} group - Group index
   * @param {number} page - Page within that group
   * @returns {URL}
   */
  #stockSortUrl(group, page) {
    const { base, param, groups } = /** @type {NonNullable<typeof this.#stockSort>} */ (this.#stockSort);
    const url = new URL(base.href);
    url.searchParams.delete(param);
    url.searchParams.append(param, groups[group]?.value ?? '');
    url.searchParams.set('page', page.toString());
    url.hash = '';
    return url;
  }

  /**
   * Next page in stock sort order: the rest of the current group, then page 1 of the next group.
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL, group: number } | undefined}
   */
  #getStockSortPage(type) {
    const stockSort = this.#stockSort;
    if (!stockSort || type === 'previous') return;

    const cards = this.querySelectorAll('[ref="grid"] > [ref="cards[]"]');
    const lastCard = /** @type {HTMLElement | undefined} */ (cards[cards.length - 1]);
    if (!lastCard || !lastCard.hasAttribute('data-ah-group')) return;

    let group = Number(lastCard.dataset.ahGroup);
    let page = Number(lastCard.dataset.page) + 1;
    if (page > (stockSort.groups[group]?.lastPage ?? 0)) {
      group += 1;
      page = 1;
    }
    if (group >= stockSort.groups.length) return;

    return { page, url: this.#stockSortUrl(group, page), group };
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
